import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getAuth } from 'firebase/auth';
import { jwtDecode } from 'jwt-decode';
import toast from 'react-hot-toast';
import {
  getExpedienteDeMascota,
  obtenerInfoMascota,
  actualizarVacuna,
  eliminarVacuna,
  eliminarEntradaExpediente,
  verificarRecordatoriosEntrada,
  obtenerTratamientosMascota,
  obtenerVacunasDeMascota,
  obtenerDesparasitacionesDeMascota,
  obtenerPerfilProfesional,
  obtenerRecordatoriosPersonales,
  exportarExpedientePdf,
  exportarCartillaPdf,
} from "../services/api";
import ControlPeso from "./expediente/ControlPeso";
import CartillaSalud from "./expediente/CartillaSalud";
import HistorialVeterinario from "./expediente/HistorialVeterinario";
import VisitaDetalle from "./expediente/VisitaDetalle";
import RegistrarVisita from "./expediente/RegistrarVisita";
import EditVacunaModal from "./expediente/EditVacunaModal";
import ExportPdfModal from "./ExportPdfModal";
import ImportCartillaModal from "./ImportCartillaModal";
import QuickReminder from "../userPanel/components/QuickReminder";
import ModalGenerarRecordatorios from "../tratamientos/ModalGenerarRecordatorios";

export default function ExpedienteClinicoPanel({ onClose }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const objetoId = searchParams.get("objetoId");
  const vetIdFromUrl = searchParams.get("vetId");
  const planFromUrl = searchParams.get("plan");
  const viewFromUrl = searchParams.get("view");
  const cartillaOnly = viewFromUrl === 'cartilla';

  const isVetAccess = Boolean(vetIdFromUrl);
  const isLightPlan = planFromUrl === 'light';
  const isStandardPlan = planFromUrl === 'standard';
  const isPremiumPlan = planFromUrl === 'premium';
  const canUseIA = isVetAccess || isPremiumPlan;

  const obtenerUsuarioActual = () => {
    const uidFromUrl = searchParams.get("uid");
    const tokenFromUrl = searchParams.get("token");
    if (uidFromUrl && tokenFromUrl) {
      return { ownerId: uidFromUrl, token: tokenFromUrl };
    }
    const nfcToken = localStorage.getItem('nfcToken');
    if (nfcToken) {
      try {
        const decoded = jwtDecode(nfcToken);
        const uid = decoded.uid || decoded.sub || decoded.userId || decoded.user_id;
        return { ownerId: uid };
      } catch (error) {
        console.error("Error decodificando token NFC:", error);
      }
    }
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (currentUser) {
        return { ownerId: currentUser.uid };
      }
    } catch (error) { /* Firebase Auth no disponible */ }
    return { ownerId: "" };
  };

  const usuario = obtenerUsuarioActual();

  // === ESTADOS COMPARTIDOS ===
  const [entradas, setEntradas] = useState([]);
  const [, setSelectedEntryId] = useState(null);
  const [, setPresetTipo] = useState(null);
  const [mascotaInfo, setMascotaInfo] = useState(null);
  const [, setLoadingMascota] = useState(true);
  const [tratamientosActivos, setTratamientosActivos] = useState([]);
  const [cartillaSectionOpen, setCartillaSectionOpen] = useState(cartillaOnly);

  // Modal de visita médica
  const [showVisitaModal, setShowVisitaModal] = useState(false);
  const [visitaModalData, setVisitaModalData] = useState(null);
  const [showRegistrarVisita, setShowRegistrarVisita] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Compartir y exportar
  const [loadingShare] = useState(false);
  const [lugaresGuardados] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingCartillaExport, setLoadingCartillaExport] = useState(false);

  // Recordatorios gamificados
  const [showRecordatoriosModal, setShowRecordatoriosModal] = useState(false);
  const [selectedEntryForRecordatorios, setSelectedEntryForRecordatorios] = useState(null);
  const [entradasConRecordatorios, setEntradasConRecordatorios] = useState({});
  const [postSaveTreatmentEntry, setPostSaveTreatmentEntry] = useState(null);

  // Vacunas y desparasitaciones
  const [vacunas, setVacunas] = useState([]);
  const [loadingVacunas, setLoadingVacunas] = useState(true);
  const [showImportCartillaModal, setShowImportCartillaModal] = useState(false);
  const [editingVacuna, setEditingVacuna] = useState(null);
  const [savingVacuna, setSavingVacuna] = useState(false);
  const [desparasitaciones, setDesparasitaciones] = useState([]);
  const [loadingDesparasitaciones, setLoadingDesparasitaciones] = useState(true);

  // Próximas con recordatorio
  const [proximasConRecordatorio, setProximasConRecordatorio] = useState({});
  const [creandoRecordatorio, setCreandoRecordatorio] = useState({});

  // QuickReminder y perfil veterinario
  const [showQuickReminder, setShowQuickReminder] = useState(false);
  const [perfilVeterinario, setPerfilVeterinario] = useState(null);
  const [, setLoadingPerfilVet] = useState(false);

  // === USE EFFECTS ===

  useEffect(() => {
    async function cargarPerfilVeterinario() {
      if (!isVetAccess) return;
      setLoadingPerfilVet(true);
      try {
        const resp = await obtenerPerfilProfesional();
        if (!resp.error && resp.data) {
          setPerfilVeterinario(resp.data);
          if (!resp.data.perfilCompleto) {
            toast.error('Completa tu perfil profesional para poder registrar visitas medicas', {
              duration: 6000, icon: '⚠️'
            });
          }
        }
      } catch (error) {
        console.error('[cargarPerfilVeterinario] Error:', error);
      } finally {
        setLoadingPerfilVet(false);
      }
    }
    cargarPerfilVeterinario();
  }, [isVetAccess]);

  useEffect(() => {
    async function cargarExpediente() {
      if (!objetoId) return;
      const resp = await getExpedienteDeMascota(objetoId);
      if (!resp.error) {
        setEntradas(resp.data);
        verificarRecordatoriosDeEntradas(resp.data);
      } else {
        console.error("Error al cargar expediente:", resp.error);
      }
    }
    cargarExpediente();
  }, [objetoId]);

  useEffect(() => {
    async function cargarInfoMascota() {
      if (!objetoId) return;
      setLoadingMascota(true);
      try {
        const resp = await obtenerInfoMascota(objetoId);
        if (!resp.error && resp.data) setMascotaInfo(resp.data);
      } catch (error) {
        console.error("Error al cargar info mascota:", error);
      } finally {
        setLoadingMascota(false);
      }
    }
    cargarInfoMascota();
  }, [objetoId]);

  useEffect(() => {
    async function cargarTratamientos() {
      if (!objetoId) return;
      try {
        const resp = await obtenerTratamientosMascota(objetoId);
        if (!resp.error && resp.data) {
          const activos = resp.data.filter(t => t.estado === 'activo' || t.estado === 'en_progreso');
          setTratamientosActivos(activos);
        }
      } catch (error) {
        console.error("Error al cargar tratamientos:", error);
      }
    }
    cargarTratamientos();
  }, [objetoId]);

  useEffect(() => {
    async function cargarVacunas() {
      if (!objetoId) return;
      setLoadingVacunas(true);
      try {
        const resp = await obtenerVacunasDeMascota(objetoId);
        if (!resp.error && resp.data) setVacunas(resp.data);
      } catch (error) {
        console.error("Error al cargar vacunas:", error);
      } finally {
        setLoadingVacunas(false);
      }
    }
    cargarVacunas();
  }, [objetoId]);

  useEffect(() => {
    async function cargarDesparasitaciones() {
      if (!objetoId) return;
      setLoadingDesparasitaciones(true);
      try {
        const resp = await obtenerDesparasitacionesDeMascota(objetoId);
        if (!resp.error && resp.data) setDesparasitaciones(resp.data);
      } catch (error) {
        console.error("Error al cargar desparasitaciones:", error);
      } finally {
        setLoadingDesparasitaciones(false);
      }
    }
    cargarDesparasitaciones();
  }, [objetoId]);

  useEffect(() => {
    async function cargarRecordatoriosExistentes() {
      if (!objetoId || isVetAccess) return;
      try {
        const resp = await obtenerRecordatoriosPersonales(objetoId);
        if (!resp.error && resp.data) {
          const existentes = {};
          resp.data.filter(r => r.activo !== false).forEach(r => {
            if (r.que && r.fecha) existentes[`${r.que}|${r.fecha}`] = true;
          });
          setProximasConRecordatorio(prev => ({ ...prev, ...existentes }));
        }
      } catch (error) { /* Non-critical */ }
    }
    cargarRecordatoriosExistentes();
  }, [objetoId, isVetAccess]);

  useEffect(() => {
    async function cargarTratamientosEntradas() {
      if (!objetoId || isVetAccess) return;
      try {
        const resp = await obtenerTratamientosMascota(objetoId, 'activo');
        if (!resp.error && resp.tratamientos) {
          const mapa = {};
          resp.tratamientos.forEach(t => {
            if (t.entradaExpedienteId) {
              mapa[t.entradaExpedienteId] = {
                tratamientoId: t.id,
                diaActual: t.diaActual || 1,
                diasTotales: t.diasTotales || 0,
                racha: t.racha || 0
              };
            }
          });
          setEntradasConRecordatorios(mapa);
        }
      } catch (error) { /* Non-critical */ }
    }
    cargarTratamientosEntradas();
  }, [objetoId, isVetAccess]);

  // === USE MEMO ===

  const pesoChartData = useMemo(() => {
    if (!entradas || entradas.length === 0) return [];
    return entradas
      .filter(e => e.peso && !isNaN(parseFloat(e.peso)))
      .map(e => ({
        id: e.id,
        fecha: e.fecha,
        peso: parseFloat(e.peso),
        createdAt: e.createdAt,
        fechaFormateada: new Date(e.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })
      }))
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  }, [entradas]);

  const ultimoPeso = useMemo(() => {
    if (pesoChartData.length === 0) return null;
    return pesoChartData[pesoChartData.length - 1].peso;
  }, [pesoChartData]);

  const cartillaData = useMemo(() => {
    const hoy = new Date().toISOString().split('T')[0];
    const vacunasAplicadas = vacunas.filter(v => v.estado !== 'programada').sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    const despAplicadas = desparasitaciones.filter(d => d.estado !== 'programada').sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    const proximas = [];

    vacunas.filter(v => v.estado === 'programada').forEach(v => {
      proximas.push({ id: v.id, tipo: 'vacuna', nombre: v.nombre, fechaProgramada: v.fechaProgramada, estado: 'programada', vencida: v.fechaProgramada < hoy, origen: v });
    });

    vacunasAplicadas.forEach(v => {
      if (!v.proximaVacunacion) return;
      const yaCumplida = vacunasAplicadas.some(otra => otra.nombre === v.nombre && otra.fecha >= v.proximaVacunacion);
      const yaProgrmada = proximas.some(p => p.tipo === 'vacuna' && p.nombre === v.nombre);
      if (!yaCumplida && !yaProgrmada) {
        proximas.push({ id: `prox_vac_${v.id}`, tipo: 'vacuna', nombre: v.nombre, fechaProgramada: v.proximaVacunacion, estado: 'proximaVacunacion', vencida: v.proximaVacunacion < hoy, origenVacunaId: v.id, origen: v });
      }
    });

    desparasitaciones.filter(d => d.estado === 'programada').forEach(d => {
      proximas.push({ id: d.id, tipo: 'desparasitacion', nombre: d.producto, fechaProgramada: d.fechaProgramada, estado: 'programada', vencida: d.fechaProgramada < hoy, origen: d });
    });

    despAplicadas.forEach(d => {
      if (!d.proximaDesparasitacion) return;
      const yaCumplida = despAplicadas.some(otra => otra.producto === d.producto && otra.fecha >= d.proximaDesparasitacion);
      const yaProgramada = proximas.some(p => p.tipo === 'desparasitacion' && p.nombre === d.producto);
      if (!yaCumplida && !yaProgramada) {
        proximas.push({ id: `prox_desp_${d.id}`, tipo: 'desparasitacion', nombre: d.producto, fechaProgramada: d.proximaDesparasitacion, estado: 'proximaDesparasitacion', vencida: d.proximaDesparasitacion < hoy, origenDesparasitacionId: d.id, origen: d });
      }
    });

    proximas.sort((a, b) => new Date(a.fechaProgramada) - new Date(b.fechaProgramada));
    return { vacunas: vacunasAplicadas, desparasitaciones: despAplicadas, proximas };
  }, [vacunas, desparasitaciones]);

  const historialVeterinario = useMemo(() => {
    if (!entradas || entradas.length === 0) return {};
    return entradas.reduce((grupos, entrada) => {
      const tipo = entrada.tipo || 'Sin Tipo';
      if (!grupos[tipo]) grupos[tipo] = [];
      grupos[tipo].push(entrada);
      return grupos;
    }, {});
  }, [entradas]);

  // === HANDLERS ===

  async function verificarRecordatoriosDeEntradas(entradasData) {
    const mapRecordatorios = {};
    for (const entrada of entradasData) {
      const medicamentosValidos = entrada.medicamentos?.filter(m => m.frecuenciaHoras && m.duracionDias);
      if (medicamentosValidos?.length > 0) {
        const result = await verificarRecordatoriosEntrada(objetoId, entrada.id);
        if (result.tieneRecordatorios && result.tratamientoId) {
          mapRecordatorios[entrada.id] = result.tratamientoId;
        }
      }
    }
    setEntradasConRecordatorios(mapRecordatorios);
  }

  function handleAddNew() {
    if (isVetAccess && (!perfilVeterinario || !perfilVeterinario.perfilCompleto)) {
      toast.error('Completa tu perfil profesional antes de registrar visitas medicas', { duration: 5000, icon: '⚠️' });
      return;
    }
    if (canUseIA) {
      setShowRegistrarVisita(true);
    } else {
      setVisitaModalData({ isNew: true });
      setShowVisitaModal(true);
    }
  }

  function handleAddQuickEntry(tipo) {
    setPresetTipo(tipo);
    setVisitaModalData({ isNew: true, tipo: tipo });
    setShowVisitaModal(true);
  }

  function handleOpenVisitaModal(entryId) {
    const entry = entradas.find(e => e.id === entryId);
    if (entry) {
      setVisitaModalData(entry);
      setShowVisitaModal(true);
    }
  }

  function handleCloseVisitaModal() {
    setShowVisitaModal(false);
    setVisitaModalData(null);
  }

  function handleBack() {
    if (onClose) { onClose(); } else { window.history.back(); }
  }

  function handleCompartir() {
    const uid = searchParams.get("uid");
    const token = searchParams.get("token");
    navigate(`/cartilla?objetoId=${encodeURIComponent(objetoId)}&uid=${uid}&token=${token}`);
  }

  function handleRecordatoriosSuccess(result) {
    if (result.tratamientoId && selectedEntryForRecordatorios) {
      setEntradasConRecordatorios(prev => ({
        ...prev,
        [selectedEntryForRecordatorios.id]: {
          tratamientoId: result.tratamientoId,
          diaActual: 1,
          diasTotales: result.diasTotales || 0,
          racha: 0
        }
      }));
    }
    setShowRecordatoriosModal(false);
    setSelectedEntryForRecordatorios(null);
  }

  function handleVerDashboardTratamiento(tratamientoId) {
    window.open(`/tratamientos/${tratamientoId}`, '_blank');
  }

  function handleStartTratamiento(entry) {
    setSelectedEntryForRecordatorios(entry);
    setShowRecordatoriosModal(true);
  }

  async function handleVacunasImportadas(nuevasVacunas, visitasCreadas) {
    setVacunas(prev => [...nuevasVacunas, ...prev]);
    if (visitasCreadas && visitasCreadas.length > 0) {
      const resp = await getExpedienteDeMascota(objetoId);
      if (!resp.error) setEntradas(resp.data);
    }
    setShowImportCartillaModal(false);
  }

  async function handleDesparasitacionesImportadas(nuevasDesparasitaciones) {
    setDesparasitaciones(prev => [...nuevasDesparasitaciones, ...prev]);
  }

  // Vacuna handlers (para VisitaDetalle > ExpedienteClinicoForm)
  async function handleSaveVacuna(vacunaId, updates) {
    setSavingVacuna(true);
    const resp = await actualizarVacuna(objetoId, vacunaId, updates);
    setSavingVacuna(false);
    if (!resp.error) {
      setVacunas(prev => prev.map(v => v.id === vacunaId ? { ...v, ...updates } : v));
      setEditingVacuna(null);
      toast.success('Vacuna actualizada');
    } else {
      toast.error(resp.error);
    }
  }

  async function handleDeleteVacuna(vacunaId) {
    if (!vacunaId) { toast.error('Error: ID de vacuna no válido'); return; }
    const vacuna = vacunas.find(v => v.id === vacunaId);
    const eventoMedicoId = vacuna?.eventoMedicoId;
    let visitaQuedaraVacia = false;
    let entrada = null;
    if (eventoMedicoId) {
      entrada = entradas.find(e => e.id === eventoMedicoId);
      const otrasVacunas = vacunas.filter(v => v.eventoMedicoId === eventoMedicoId && v.id !== vacunaId);
      const despAsociadas = desparasitaciones.filter(d => d.eventoMedicoId === eventoMedicoId);
      const tieneOtros = entrada?.tipos?.some(t => t !== 'vacunacion' && t !== 'desparasitacion');
      visitaQuedaraVacia = otrasVacunas.length === 0 && despAsociadas.length === 0 && !tieneOtros;
    }
    let mensaje = '¿Eliminar esta vacuna del registro?';
    if (visitaQuedaraVacia && entrada) {
      const fecha = new Date(entrada.fecha).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
      mensaje = `Esta es la última vacuna de la visita del ${fecha}.\n\n¿Eliminar la vacuna Y la visita?`;
    }
    if (!window.confirm(mensaje)) return;
    try {
      const resp = await eliminarVacuna(objetoId, vacunaId);
      if (resp.error) { toast.error(resp.error); return; }
      setVacunas(prev => prev.filter(v => v.id !== vacunaId));
      if (visitaQuedaraVacia && eventoMedicoId) {
        const respVisita = await eliminarEntradaExpediente(objetoId, eventoMedicoId);
        if (!respVisita.error) {
          setEntradas(prev => prev.filter(e => e.id !== eventoMedicoId));
          toast.success('Vacuna y visita eliminadas');
        } else {
          toast.success('Vacuna eliminada (la visita no pudo ser eliminada)');
        }
      } else {
        toast.success('Vacuna eliminada');
      }
    } catch (error) {
      console.error('[handleDeleteVacuna] Exception:', error);
      toast.error('Error al eliminar la vacuna');
    }
  }

  // Export handlers
  async function handleExportPdf(entryIds, incluirDocumentos, lugaresConfianza = []) {
    setLoadingExport(true);
    const resp = await exportarExpedientePdf(objetoId, entryIds, incluirDocumentos, lugaresConfianza);
    if (resp.error) { toast.error(resp.error); setLoadingExport(false); return; }
    try {
      const url = window.URL.createObjectURL(resp.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Expediente_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Expediente exportado exitosamente');
      setShowExportModal(false);
    } catch (error) {
      console.error('Error descargando PDF:', error);
      toast.error('Error al descargar el PDF');
    }
    setLoadingExport(false);
  }

  async function handleExportCartilla() {
    setLoadingCartillaExport(true);
    const resp = await exportarCartillaPdf(objetoId);
    if (resp.error) { toast.error(resp.error); setLoadingCartillaExport(false); return; }
    try {
      const url = window.URL.createObjectURL(resp.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Cartilla_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Cartilla de salud exportada');
    } catch (error) {
      console.error('Error descargando cartilla PDF:', error);
      toast.error('Error al descargar la cartilla');
    }
    setLoadingCartillaExport(false);
  }

  // === RENDER ===

  return (
    <div
      className="expediente-overlay"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 99999, backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'auto', padding: 'var(--space-4)'
      }}
    >
      <div
        className="card card-elevated expediente-modal"
        style={{
          borderRadius: 'var(--radius-xl)', maxWidth: '900px', width: '100%',
          maxHeight: '90vh', overflow: 'hidden', display: 'flex',
          flexDirection: 'column', margin: 0
        }}
      >
        {/* Header con gradiente */}
        <div
          className="modal-header-gradient d-flex justify-content-between align-items-center"
          style={{ paddingTop: 'max(16px, calc(16px + env(safe-area-inset-top, 0px)))' }}
        >
          <h5 className="mb-0" style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--white)' }}>
            <i className={`bi ${cartillaOnly ? 'bi-journal-medical' : 'bi-heart-pulse-fill'} me-2`}></i>
            {cartillaOnly
              ? `Cartilla de Salud${mascotaInfo?.nombreMascota ? ` — ${mascotaInfo.nombreMascota}` : ''}`
              : 'Expediente Clínico'}
          </h5>
          <button type="button" className="btn-close btn-close-white" onClick={handleBack}></button>
        </div>

        {/* Body scrollable */}
        <div className="card-body" style={{ padding: 'var(--space-4)', overflow: 'auto', flex: 1 }}>
          {isLightPlan ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-6) var(--space-4)', maxWidth: '500px', margin: '0 auto' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-4)' }}>
                <i className="bi bi-lock-fill" style={{ fontSize: '32px', color: '#9ca3af' }}></i>
              </div>
              <h4 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--gray-900)', marginBottom: 'var(--space-3)' }}>
                Expediente Clínico no disponible
              </h4>
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--gray-600)', marginBottom: 'var(--space-4)', lineHeight: '1.6' }}>
                El Expediente Clínico y la Cartilla de Salud están disponibles en los planes <strong>Standard</strong> y <strong>Premium</strong>.
              </p>
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)', marginBottom: 'var(--space-4)' }}>
                Con estos planes podrás:
              </p>
              <ul style={{ textAlign: 'left', fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginBottom: 'var(--space-4)', paddingLeft: 'var(--space-4)' }}>
                <li style={{ marginBottom: '8px' }}>Registrar visitas médicas y vacunas</li>
                <li style={{ marginBottom: '8px' }}>Llevar un historial de salud completo</li>
                <li style={{ marginBottom: '8px' }}>Compartir el expediente con veterinarios</li>
                <li>Importar cartillas de vacunación desde foto (Premium)</li>
              </ul>
              <button className="btn btn-feature" onClick={() => window.location.href = '/planes'} style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', padding: 'var(--space-3) var(--space-4)' }}>
                <i className="bi bi-arrow-up-circle me-2"></i>
                Ver planes
              </button>
            </div>
          ) : (
          <>
          {/* Botones superiores */}
          {!cartillaOnly && (
          <div className="d-flex gap-2 mb-4 align-items-center">
            <button
              className="btn transition-base"
              onClick={handleAddNew}
              style={{ flex: 1, fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', padding: '12px var(--space-3)', backgroundColor: '#1f2937', color: '#fff', border: 'none', borderRadius: '10px' }}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Registrar visita médica
            </button>
            {(!isVetAccess || entradas.length > 0) && (
              <div style={{ position: 'relative' }}>
                <button
                  className="btn transition-base"
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  style={{ width: '44px', height: '44px', padding: 0, backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <i className="bi bi-three-dots-vertical" style={{ fontSize: '18px' }}></i>
                </button>
                {showActionsMenu && (
                  <>
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setShowActionsMenu(false)}></div>
                    <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '4px', backgroundColor: '#fff', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', minWidth: '200px', zIndex: 1000, overflow: 'hidden' }}>
                      {!isVetAccess && (
                        <button className="btn w-100 text-start" onClick={() => { setShowActionsMenu(false); handleCompartir(); }} disabled={loadingShare} style={{ padding: '12px 16px', fontSize: 'var(--font-size-sm)', color: '#374151', border: 'none', borderRadius: 0, backgroundColor: 'transparent' }}>
                          <i className="bi bi-share me-2" style={{ color: '#22c55e' }}></i>
                          Compartir Cartilla
                        </button>
                      )}
                      {entradas.length > 0 && (
                        <button className="btn w-100 text-start" onClick={() => { setShowActionsMenu(false); setShowExportModal(true); }} disabled={loadingExport} style={{ padding: '12px 16px', fontSize: 'var(--font-size-sm)', color: '#374151', border: 'none', borderRadius: 0, backgroundColor: 'transparent', borderTop: !isVetAccess ? '1px solid #f3f4f6' : 'none' }}>
                          <i className="bi bi-file-earmark-pdf me-2" style={{ color: '#ef4444' }}></i>
                          Exportar a PDF
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          )}

          {/* Banner de Alergias */}
          {mascotaInfo?.alergias && (
            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)', marginBottom: 'var(--space-3)', display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
              <i className="bi bi-exclamation-triangle-fill" style={{ color: '#dc2626', fontSize: '18px' }}></i>
              <div>
                <strong style={{ color: '#991b1b', fontSize: 'var(--font-size-sm)' }}>Alergias:</strong>
                <span style={{ color: '#7f1d1d', fontSize: 'var(--font-size-sm)', marginLeft: '8px' }}>{mascotaInfo.alergias}</span>
              </div>
            </div>
          )}

          {/* Cards de secciones */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {!cartillaOnly && (
              <ControlPeso
                pesoChartData={pesoChartData}
                ultimoPeso={ultimoPeso}
                mascotaInfo={mascotaInfo}
                objetoId={objetoId}
                onPesoRegistered={(newEntry) => {
                  setEntradas(prev => [...prev, newEntry]);
                }}
                onPesoUpdated={(entryId, newPeso) => {
                  setEntradas(prev => prev.map(e =>
                    e.id === entryId ? { ...e, peso: newPeso } : e
                  ));
                }}
                onPesoDeleted={(entryId) => {
                  setEntradas(prev => prev.filter(e => e.id !== entryId));
                }}
              />
            )}

            <CartillaSalud
              cartillaSectionOpen={cartillaSectionOpen}
              setCartillaSectionOpen={setCartillaSectionOpen}
              cartillaData={cartillaData}
              vacunas={vacunas}
              setVacunas={setVacunas}
              desparasitaciones={desparasitaciones}
              setDesparasitaciones={setDesparasitaciones}
              entradas={entradas}
              setEntradas={setEntradas}
              objetoId={objetoId}
              mascotaInfo={mascotaInfo}
              isVetAccess={isVetAccess}
              canUseIA={canUseIA}
              isStandardPlan={isStandardPlan}
              loadingVacunas={loadingVacunas}
              loadingDesparasitaciones={loadingDesparasitaciones}
              tratamientosActivos={tratamientosActivos}
              proximasConRecordatorio={proximasConRecordatorio}
              setProximasConRecordatorio={setProximasConRecordatorio}
              creandoRecordatorio={creandoRecordatorio}
              setCreandoRecordatorio={setCreandoRecordatorio}
              onOpenVisitaModal={handleOpenVisitaModal}
              onAddQuickEntry={handleAddQuickEntry}
              onCompartir={handleCompartir}
              onExportCartilla={handleExportCartilla}
              loadingShare={loadingShare}
              loadingCartillaExport={loadingCartillaExport}
              onShowImportCartilla={() => setShowImportCartillaModal(true)}
              onShowQuickReminder={() => setShowQuickReminder(true)}
              onVerDashboardTratamiento={handleVerDashboardTratamiento}
            />

            {!cartillaOnly && (
              <HistorialVeterinario
                historialVeterinario={historialVeterinario}
                entradasConRecordatorios={entradasConRecordatorios}
                isVetAccess={isVetAccess}
                onOpenVisitaModal={handleOpenVisitaModal}
                onVerDashboardTratamiento={handleVerDashboardTratamiento}
                onStartTratamiento={handleStartTratamiento}
              />
            )}
          </div>
          </>
          )}
        </div>
      </div>

      {/* === MODALES === */}

      <VisitaDetalle
        show={showVisitaModal}
        visitaModalData={visitaModalData}
        onClose={handleCloseVisitaModal}
        objetoId={objetoId}
        isVetAccess={isVetAccess}
        usuario={usuario}
        entradas={entradas}
        setEntradas={setEntradas}
        vacunas={vacunas}
        setVacunas={setVacunas}
        desparasitaciones={desparasitaciones}
        setDesparasitaciones={setDesparasitaciones}
        entradasConRecordatorios={entradasConRecordatorios}
        perfilVeterinario={perfilVeterinario}
        onEditVacuna={!isVetAccess ? setEditingVacuna : null}
        onDeleteVacuna={!isVetAccess ? handleDeleteVacuna : null}
        onVerDashboardTratamiento={handleVerDashboardTratamiento}
        onStartTratamiento={handleStartTratamiento}
        onPostSaveTreatment={setPostSaveTreatmentEntry}
        setVisitaModalData={setVisitaModalData}
        setPresetTipo={setPresetTipo}
        setSelectedEntryId={setSelectedEntryId}
      />

      {showRegistrarVisita && (
        <RegistrarVisita
          show={true}
          objetoId={objetoId}
          usuario={usuario}
          isVetAccess={isVetAccess}
          onClose={() => setShowRegistrarVisita(false)}
          onOpenManualEntry={() => {
            setShowRegistrarVisita(false);
            setVisitaModalData({ isNew: true });
            setShowVisitaModal(true);
          }}
          onEntryCreated={(newEntry) => {
            setEntradas(prev => [...prev, newEntry]);
          }}
          onPostSaveTreatment={setPostSaveTreatmentEntry}
        />
      )}

      <ExportPdfModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        entradas={entradas}
        onExport={handleExportPdf}
        loading={loadingExport}
        lugaresGuardados={lugaresGuardados}
      />

      <ModalGenerarRecordatorios
        show={showRecordatoriosModal}
        onClose={() => { setShowRecordatoriosModal(false); setSelectedEntryForRecordatorios(null); }}
        medicamentos={selectedEntryForRecordatorios?.medicamentos || []}
        objetoId={objetoId}
        entradaExpedienteId={selectedEntryForRecordatorios?.id}
        onSuccess={handleRecordatoriosSuccess}
      />

      {/* Prompt post-guardado para crear tratamiento */}
      {postSaveTreatmentEntry && (
        <div
          className="modal-overlay"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1060, padding: '20px' }}
          onClick={() => setPostSaveTreatmentEntry(null)}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '400px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '36px' }}>💊</span>
              <h4 style={{ margin: '8px 0 4px', color: '#1f2937', fontSize: '16px' }}>Crear plan de tratamiento</h4>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Esta visita tiene medicamentos con dosis y duración. ¿Quieres activar recordatorios para no olvidar ninguna toma?</p>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={() => setPostSaveTreatmentEntry(null)} style={{ flex: 1, padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#fff', color: '#374151', fontSize: '14px', cursor: 'pointer' }}>
                Ahora no
              </button>
              <button onClick={() => { setSelectedEntryForRecordatorios(postSaveTreatmentEntry); setShowRecordatoriosModal(true); setPostSaveTreatmentEntry(null); }} style={{ flex: 1, padding: '10px', border: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #4CAF50, #7AB43B)', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                Activar recordatorios
              </button>
            </div>
          </div>
        </div>
      )}

      <ImportCartillaModal
        show={showImportCartillaModal}
        onClose={() => setShowImportCartillaModal(false)}
        objetoId={objetoId}
        onImportSuccess={handleVacunasImportadas}
        onDesparasitacionesImportadas={handleDesparasitacionesImportadas}
        desparasitacionesExistentes={cartillaData.desparasitaciones}
      />

      {showQuickReminder && (
        <QuickReminder
          objetoId={objetoId}
          objetoNombre={mascotaInfo?.nombreMascota || mascotaInfo?.nombre || 'Mascota'}
          objetoTipo="mascota"
          objetoEspecie={mascotaInfo?.especie || ''}
          objetoPlan={planFromUrl || 'light'}
          userId={usuario?.ownerId}
          onClose={() => setShowQuickReminder(false)}
          onSuccess={() => setShowQuickReminder(false)}
        />
      )}

      <EditVacunaModal
        vacuna={editingVacuna}
        onClose={() => setEditingVacuna(null)}
        onSave={handleSaveVacuna}
        saving={savingVacuna}
      />

      {/* Estilos responsivos para iOS Safari */}
      <style>{`
        @media (max-width: 767px) {
          .expediente-overlay {
            padding: 0 !important;
            padding-top: env(safe-area-inset-top, 0px) !important;
            padding-bottom: env(safe-area-inset-bottom, 0px) !important;
            align-items: stretch !important;
          }
          .expediente-modal {
            max-width: 100% !important;
            width: 100% !important;
            height: 100% !important;
            max-height: 100% !important;
            border-radius: 0 !important;
            margin: 0 !important;
          }
          .expediente-modal .modal-header-gradient {
            padding-top: calc(var(--space-4) + env(safe-area-inset-top, 0px)) !important;
            border-radius: 0 !important;
            flex-shrink: 0;
          }
          .visita-modal-content {
            max-width: 100% !important;
            width: 100% !important;
            height: 100% !important;
            max-height: 100% !important;
            border-radius: 0 !important;
            margin: 0 !important;
          }
          .visita-modal-content .modal-header-gradient {
            padding-top: calc(var(--space-3) + env(safe-area-inset-top, 0px)) !important;
            border-radius: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}
